import {
  Stack,
  Tooltip,
  Box,
  Flex,
  useDisclosure,
  IconButton,
} from "@chakra-ui/react";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  memo,
  useMemo,
} from "react";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { getGaraazStateColor, OrderActions, OrderStatus } from "../enums";
import { AllocatedTo, startOrcaStates } from "./GaraazOrderComponent";
import { removeUnderScoreWithUpperCase } from "../../../utils/removeUnderscore";
import GaraazOrderComponentHeader from "./GaraazOrderComponentHeader";
import GaraazOrderComponentBody from "./GaraazOrderComponentBody";
import GaraazOrderComponentFooter from "./GaraazOrderComponentFooter";
import GaraazOrderComponentFooterVirtual from "./GaraazOrderComponentFooterVirtual";
import GaraazOrderComponentFooterReturn from "./GaraazOrderComponentFooterReturn";
import GaraazOrderComponentFooterSubscription from "./GaraazOrderComponentFooterSubscription";
import {
  OrderLabelProvider,
  GetOrderLabels,
} from "./GaraazOrderCard/OrderLabelContext";
import { hasGaraazInvoiceUploaded, isTestGaraazOrder } from "../orderUtils";
import TestOrderBadge, { TEST_ORDER_COLOR } from "./GaraazOrderCard/TestOrderBadge";
import GaraazOrderVehicleInfoStrip, {
  hasVehicleInfoStripData,
} from "./GaraazOrderVehicleInfoStrip";

type GaraazOrderCardProps = {
  orderData: any;
  orderHandlers: any;
  orderCancellationReason: string[];
  refetchAllOrders: () => void;
  isChildOrder?: boolean;
  onToggleChildOrders: (orderId: string) => void;
  reFetchChildOrders: () => void;
  isChildOrdersVisible: boolean;
  findBrandName: (brandId: string) => string;
  getSupplierBusinessName: (supplierId: string) => string;
  getSupplierParentId: (supplierId: string) => string;
  getSupplierBranchName: (supplierId: string, branchId: string) => string;
  getSupplierNumber: (supplierId: string, branchId?: string) => string;
  isCustomerOrderFreezed?: boolean;
  isPartsFreezed?: boolean;
};

const GaraazOrderCard: React.FC<GaraazOrderCardProps> = memo(
  ({
    orderData,
    orderCancellationReason,
    refetchAllOrders,
    isChildOrder = false,
    onToggleChildOrders,
    reFetchChildOrders,
    isChildOrdersVisible,
    findBrandName,
    getSupplierBusinessName,
    getSupplierParentId,
    getSupplierBranchName,
    getSupplierNumber,
    isCustomerOrderFreezed = false,
    isPartsFreezed = false,
  }) => {
    const [isBodyVisible, setIsBodyVisible] = useState(
      isChildOrder
        ? false
        : orderData.orderType === "SUBSCRIPTION" ||
            startOrcaStates.includes(orderData.orderStatus) ||
            orderData.orderStatus === OrderStatus.READY_TO_DISPATCH ||
            orderData.orderStatus === OrderStatus.DELIVERED
          ? false
          : true,
    );

    const [orderStatus, setOrderStatus] = useState(
      orderData?.orderStatus ?? "",
    );
    const [transactionType, setTransactionType] = useState(
      orderData?.transactionType,
    );
    const [displayPartConversion, setDisplayPartConversion] = useState("flex");
    const [isEstimateRequired, setIsEstimateRequired] = useState(
      orderData?.requireEstimate,
    );
    const [garaazRemarks, setGaraazRemarks] = useState(orderData.garaazRemarks);
    const [supplierCancellationReasons, setSupplierCancellationReasons] =
      useState(orderData.supplierCancellationReasons);
    const [customerHoldReasons, setCustomerHoldReasons] = useState(
      orderData.customerHoldReasons,
    );
    const [promiseTimeBreachReasons, setPromiseTimeBreachReasons] = useState(
      orderData.promiseTimeBreachReasons,
    );
    const [etaMissed, setEtaMissed] = useState(false);

    const {
      isOpen: etaPickerModalOpen,
      onOpen: openETAPickerModal,
      onClose: closeETAPickerModal,
    } = useDisclosure();

    const activesupplierOrderStatus: AllocatedTo = orderData?.allocatedTo?.find(
      (item: AllocatedTo) => item.isActive,
    );

    useEffect(() => {
      setOrderStatus(orderData?.orderStatus || "");
      setTransactionType(orderData?.transactionType);
      setEtaMissed(false);
    }, [orderData?.orderStatus, orderData?.transactionType]);

    const displayOrderStatus = useMemo(() => {
      if (
        orderStatus === OrderStatus.READY_TO_DISPATCH &&
        orderData?.orderStatus !== OrderStatus.READY_TO_DISPATCH
      ) {
        return orderData?.orderStatus ?? orderStatus;
      }
      return orderStatus;
    }, [orderStatus, orderData?.orderStatus]);

    useLayoutEffect(() => {
      const effectiveStatus = displayOrderStatus;
      if (isChildOrder) {
        setIsBodyVisible(false);
      } else {
        if (
          startOrcaStates.includes(effectiveStatus) ||
          effectiveStatus === OrderStatus.READY_TO_DISPATCH ||
          effectiveStatus === OrderStatus.DELIVERED ||
          orderData.orderType === "SUBSCRIPTION"
        ) {
          setIsBodyVisible(false);
        } else {
          setIsBodyVisible(true);
        }
      }
    }, [displayOrderStatus, orderData.orderType, isChildOrder]);

    const handlePartConversionChangeScreen = useCallback(() => {
      setDisplayPartConversion((prev) => (prev === "flex" ? "none" : "flex"));
    }, []);

    const expandBody = useCallback(() => {
      setIsBodyVisible((prev) => !prev);
    }, []);

    const isEnquiryOrder = useMemo(() => {
      return (
        orderData?.requireEstimate === true &&
        orderData?.rootOrderId === orderData?._id
      );
    }, [orderData?.requireEstimate, orderData?.rootOrderId, orderData?._id]);

    const isEnquiryLocked = useMemo(() => {
      return isEnquiryOrder && hasGaraazInvoiceUploaded(orderData);
    }, [isEnquiryOrder, orderData?.invoiceInfo]);

    const orderLabel = useMemo(() => {
      return isEnquiryOrder ? GetOrderLabels.ENQUIRY : GetOrderLabels.ORDER;
    }, [isEnquiryOrder]);

    const orderStatusLabel = useMemo(
      () => removeUnderScoreWithUpperCase(displayOrderStatus ?? ""),
      [displayOrderStatus],
    );

    const isTestOrder = isTestGaraazOrder(orderData);
    const hasVehicleInfo = hasVehicleInfoStripData(orderData);
    const canToggleBody = !startOrcaStates.includes(orderStatus);
    const testOrderBorderWidth = 5;
    const testOrderInsetShadow = `inset ${testOrderBorderWidth}px 0 0 0 ${TEST_ORDER_COLOR}, inset -${testOrderBorderWidth}px 0 0 0 ${TEST_ORDER_COLOR}, inset 0 -${testOrderBorderWidth}px 0 0 ${TEST_ORDER_COLOR}`;
    // Collapsed cards size to content so header + vehicle strip + footer
    // never overlap, and ACK stays a normal-height button (not stretched).

    const collapseToggleButton = canToggleBody ? (
      <IconButton
        width="1.2em"
        height="1.2em"
        lineHeight={1}
        minWidth="none"
        aria-label={
          isBodyVisible ? "Collapse order body" : "Expand order body"
        }
        borderRadius="50%"
        onClick={expandBody}
        icon={isBodyVisible ? <BiChevronUp /> : <BiChevronDown />}
      />
    ) : null;

    return (
      <OrderLabelProvider value={{ orderLabel, isEnquiryOrder }}>
        <Stack
          id={orderData._id}
          position="relative"
          width={isChildOrder ? "calc(75vw - 55px)" : "75vw"}
          minWidth={"360px"}
          maxWidth={"1400px"}
          // Body scrolls inside this fixed height; header + LPN/tag + footer stay visible.
          height={isBodyVisible ? "65vh" : "auto"}
          overflow="visible"
          border="1px solid"
          borderColor={isTestOrder ? "orange.200" : "lightgrey"}
          bg={isTestOrder ? "orange.50" : "white"}
          borderRadius={"8px"}
          marginTop={"40px !important"}
          display={"flex"}
          flexDirection={"column"}
          className={isChildOrder ? "child-order" : "order"}
          gap={0}
          ml={isChildOrder ? 4 : 0}
          boxShadow={
            isTestOrder
              ? `${testOrderInsetShadow}, 0 2px 12px rgba(221, 107, 32, 0.12)`
              : undefined
          }
          transition="all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
          _hover={{
            ...(isTestOrder
              ? {
                  boxShadow: `${testOrderInsetShadow}, 0 8px 28px rgba(221, 107, 32, 0.22)`,
                  borderColor: "orange.300",
                }
              : {
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
                  borderColor: "teal.200",
                }),
          }}
          _focus={{
            boxShadow: "0 0 0 3px rgba(45, 212, 191, 0.2)",
          }}
        >
          {isTestOrder && <TestOrderBadge />}
          <Flex
            direction={"column"}
            gap={0}
            height={"100%"}
            minH={0}
            flex={1}
            borderRadius={"8px"}
            overflow={isBodyVisible ? "hidden" : "visible"}
          >
            <Box height={"6px"} position="relative" width="100%" flexShrink={0}>
              <Tooltip
                label={orderStatusLabel}
                placement="left-start"
                hasArrow
                cursor={"pointer"}
                color={"white"}
                bg={getGaraazStateColor(displayOrderStatus)}
                position={"relative"}
              >
                <div
                  style={{
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    background: `linear-gradient(white, white) padding-box, ${getGaraazStateColor(
                      displayOrderStatus,
                    )} border-box`,
                    borderTop: "6px solid transparent",
                    height: "6px",
                    width: "100%",
                    top: 0,
                    left: 0,
                    overflow: "hidden",
                  }}
                />
              </Tooltip>
            </Box>
            <Box height={"100%"} display="flex" flexDirection="column" minH={0}>
              <Flex direction={"column"} gap={0} height={"100%"} minH={0}>
                <Box position="relative" flexShrink={0} w="100%">
                  <GaraazOrderComponentHeader
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    etaMissed={etaMissed}
                    setEtaMissed={setEtaMissed}
                    findBrandName={findBrandName}
                    getSupplierBusinessName={getSupplierBusinessName}
                    getSupplierParentId={getSupplierParentId}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    transactionType={transactionType}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    getSupplierBranchName={getSupplierBranchName}
                    getSupplierNumber={getSupplierNumber}
                    isEstimateRequired={isEstimateRequired}
                    isUrgent={!!orderData?.isUrgent}
                    partType={orderData?.partType}
                    refetchAllOrders={refetchAllOrders}
                    onOpenCptUpdate={openETAPickerModal}
                  />
                  {/* No vehicle strip: keep toggle on header edge (avoids empty bar). */}
                  {!hasVehicleInfo && collapseToggleButton && (
                    <Box
                      position="absolute"
                      left="50%"
                      bottom={-2}
                      transform="translateX(-50%)"
                      zIndex={1}
                    >
                      {collapseToggleButton}
                    </Box>
                  )}
                </Box>

                {/* Plate/tag strip — only when vehicle info exists */}
                {hasVehicleInfo && (
                  <Box flexShrink={0} w="100%" bg="white">
                    <GaraazOrderVehicleInfoStrip orderData={orderData} />
                  </Box>
                )}

                {/* With vehicle strip: toggle sits below LPN/model row */}
                {hasVehicleInfo && collapseToggleButton && (
                  <Box
                    flexShrink={0}
                    w="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={0.5}
                    bg="white"
                    borderTop="1px solid"
                    borderColor="blackAlpha.100"
                  >
                    {collapseToggleButton}
                  </Box>
                )}

                {!startOrcaStates.includes(orderStatus) && isBodyVisible && (
                  <GaraazOrderComponentBody
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    displayPartConversion={displayPartConversion}
                    findBrandName={findBrandName}
                    getSupplierBusinessName={getSupplierBusinessName}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    garaazRemarks={garaazRemarks}
                    isEnquiryLocked={isEnquiryLocked}
                    isPartsFreezed={isPartsFreezed}
                    refetchAllOrders={refetchAllOrders}
                  />
                )}

                <Box
                  flexShrink={0}
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                >
                {orderData.orderType === "REGULAR" ? (
                  <GaraazOrderComponentFooter
                    setOrderStatus={setOrderStatus}
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    orderCancellationReason={orderCancellationReason}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    refetchAllOrders={refetchAllOrders}
                    setEtaMissed={setEtaMissed}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    garaazRemarks={garaazRemarks}
                    setGaraazRemarks={setGaraazRemarks}
                    supplierCancellationReasons={supplierCancellationReasons}
                    setSupplierCancellationReasons={
                      setSupplierCancellationReasons
                    }
                    customerHoldReasons={customerHoldReasons}
                    setCustomerHoldReasons={setCustomerHoldReasons}
                    promiseTimeBreachReasons={promiseTimeBreachReasons}
                    setPromiseTimeBreachReasons={setPromiseTimeBreachReasons}
                    setTransactionType={setTransactionType}
                    isChildOrder={isChildOrder}
                    isChildOrdersVisible={isChildOrdersVisible}
                    etaPickerModalOpen={etaPickerModalOpen}
                    closeETAPickerModal={closeETAPickerModal}
                  />
                ) : orderData.orderType === "VIRTUAL" ? (
                  <GaraazOrderComponentFooterVirtual
                    orderData={orderData}
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    garaazRemarks={garaazRemarks}
                    setGaraazRemarks={setGaraazRemarks}
                    supplierCancellationReasons={supplierCancellationReasons}
                    setSupplierCancellationReasons={
                      setSupplierCancellationReasons
                    }
                    customerHoldReasons={customerHoldReasons}
                    setCustomerHoldReasons={setCustomerHoldReasons}
                    promiseTimeBreachReasons={promiseTimeBreachReasons}
                    setPromiseTimeBreachReasons={setPromiseTimeBreachReasons}
                    onToggleChildOrders={onToggleChildOrders}
                    refetchAllOrders={refetchAllOrders}
                    reFetchChildOrders={reFetchChildOrders}
                    setTransactionType={setTransactionType}
                    isChildOrder={isChildOrder}
                    orderStatus={orderStatus}
                    setOrderStatus={setOrderStatus}
                  />
                ) : orderData.orderType === "RETURN" ? (
                  <GaraazOrderComponentFooterReturn
                    setOrderStatus={setOrderStatus}
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    orderCancellationReason={orderCancellationReason}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    refetchAllOrders={refetchAllOrders}
                    setEtaMissed={setEtaMissed}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    garaazRemarks={garaazRemarks}
                    setGaraazRemarks={setGaraazRemarks}
                    supplierCancellationReasons={supplierCancellationReasons}
                    setSupplierCancellationReasons={
                      setSupplierCancellationReasons
                    }
                    customerHoldReasons={customerHoldReasons}
                    setCustomerHoldReasons={setCustomerHoldReasons}
                    promiseTimeBreachReasons={promiseTimeBreachReasons}
                    setPromiseTimeBreachReasons={setPromiseTimeBreachReasons}
                    isChildOrder={isChildOrder}
                    isChildOrdersVisible={isChildOrdersVisible}
                    etaPickerModalOpen={etaPickerModalOpen}
                    closeETAPickerModal={closeETAPickerModal}
                  />
                ) : (
                  <GaraazOrderComponentFooterSubscription
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    orderCancellationReason={orderCancellationReason}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    refetchAllOrders={refetchAllOrders}
                    setEtaMissed={setEtaMissed}
                    setOrderStatus={setOrderStatus}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    isChildOrder={isChildOrder}
                    isChildOrdersVisible={isChildOrdersVisible}
                    etaPickerModalOpen={etaPickerModalOpen}
                    closeETAPickerModal={closeETAPickerModal}
                  />
                )}
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Stack>
      </OrderLabelProvider>
    );
  },
);

GaraazOrderCard.displayName = "GaraazOrderCard";

export default GaraazOrderCard;
